import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  TablePagination,
  TableSortLabel,
  TextField,
  Autocomplete,
  Stack,
  Button,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { getPanLogs } from "./services/PANServices";
import LogsModal from "./LogsModal";

export const formatDate = (dateString) => {
  if (!dateString) return null;
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = String(date.getFullYear()).slice(-2);
  return `${day}-${month}-${year}`;
};

export const formatTime = (dateString) => {
  if (!dateString) return null;
  const date = new Date(dateString);
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");
  return `${hours}:${minutes}:${seconds}`;
};

const Logs = ({ type, setPage, page }) => {
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("email");

  const [filters, setFilters] = useState({
    email: "",
    Vendor: "",
    Company: "",
    date: "",
  });

  const [openModal, setOpenModal] = useState(false);
  const [modalFilters, setModalFilters] = useState({});

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["PAN", { page, rowsPerPage, order, orderBy, filters, type }],
    queryFn: () =>
      getPanLogs({
        type,
        page,
        rowsPerPage,
        order,
        orderBy,
        filters,
      }),
    keepPreviousData: true,
  });

  const handleOpenModal = (filters) => {
    setOpenModal(true);
  };

  useEffect(() => {
    refetch();
  }, [filters, refetch]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSort = (column) => {
    const isAsc = orderBy === column && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(column);
  };

  const getUniqueValues = (key) => {
    const uniqueValues = [];
    (data?.[key] || []).forEach((row) => {
      if (row && !uniqueValues?.includes(row)) {
        uniqueValues.push(row);
      }
    });
    return uniqueValues;
  };

  const suggestions = {
    email: getUniqueValues("email"),
    Vendor: getUniqueValues("Vendor"),
    Company: getUniqueValues("Company"),
    date: getUniqueValues("date"),
  };

  return (
    <>
      {/* Filter Inputs */}
      <Stack direction="row" spacing={2} mb={2}>
        {["email", "Vendor", "Company"].map((field) => (
          <Autocomplete
            key={field}
            options={suggestions[field]}
            getOptionLabel={(option) => option || ""}
            renderInput={(params) => (
              <TextField
                {...params}
                label={field}
                variant="outlined"
                style={{ width: 250 }}
              />
            )}
            value={filters[field] || ""}
            onChange={(event, newValue) => {
              setPage(0);
              console.log(field, newValue);
              setFilters((prev) => ({ ...prev, [field]: newValue || "" }));
            }}
            freeSolo
          />
        ))}
      </Stack>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              {[
                { id: "email", label: "Email" },
                { id: "Vendor", label: "Vendor" },
                { id: "Company", label: "Company" },
                { id: "date", label: "Date" },
                { id: "time", label: "Time" },
              ].map((column) => (
                <TableCell key={column.id}>
                  <TableSortLabel
                    active={orderBy === column.id}
                    direction={orderBy === column.id ? order : "asc"}
                    onClick={() => handleSort(column.id)}
                  >
                    {column.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography>Loading...</Typography>
                </TableCell>
              </TableRow>
            ) : error ? (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography>Error loading data</Typography>
                </TableCell>
              </TableRow>
            ) : data?.logs.length > 0 ? (
              data?.logs.map((row, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <Button
                      onClick={() => {
                        handleOpenModal();
                        setModalFilters((prevState) => ({
                          ...prevState,
                          filterType: "email",
                          filter: row.email,
                          type: type,
                        }));
                      }}
                      sx={{
                        textTransform: "lowercase",
                      }}
                    >
                      {row.email}
                    </Button>
                  </TableCell>
                  <TableCell>{row.Vendor}</TableCell>
                  <TableCell>
                    <Button
                      onClick={() => {
                        handleOpenModal();
                        setModalFilters((prevState) => ({
                          ...prevState,
                          filterType: "company",
                          filter: row.Company,
                          type: type,
                        }));
                      }}
                      sx={{
                        textTransform: "lowercase",
                      }}
                    >
                      {row.Company}
                    </Button>
                  </TableCell>
                  <TableCell>{formatDate(row.createdAt)}</TableCell>
                  <TableCell>{formatTime(row.createdAt)}</TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography>No logs available</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={data?.totalCount || 0}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50]}
        />
      </TableContainer>

      {/* Modal */}
      <LogsModal
        filters={modalFilters}
        openModal={openModal}
        handleCloseModal={() => setOpenModal(false)}
      />
    </>
  );
};

export default Logs;
